import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { AlertService, CampaignService, UserService, FunnelService, LoaderService } from '../../../_services';
import { Form } from '../../../_forms';
import { Breadcrumb, Campaign, User, OfferIntents, OfferTypes, Pager } from '../../../_models';
import { AvailableActionHoldoffs, AvailableActionHoldOffIntents, OfferStages } from '../../../_models/funnel';
import { RetentionSettingSection, RetentionOffer } from '../retention';
import { expandCollapse, fadeInOut } from '../../../_shared/animations/app-animations';
import { takeUntil } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { getOfferBuilderSection, OfferBuilderSection, OfferBuilderSectionNames } from '../../../steps-library/step-util';

@Component({
  selector: 'app-retention-settings',
  templateUrl: './retention-settings.component.html',
  styleUrls: ['./retention-settings.component.scss'],
  animations: [expandCollapse, fadeInOut],
})
export class RetentionSettingsComponent extends Form implements OnInit {
  currentSection: OfferBuilderSection = OfferBuilderSection.Retention;
  offerBuilderSection = OfferBuilderSection;
  breadcrumbs: Breadcrumb[] = [];
  expandedSection: RetentionSettingSection = null;
  RetentionSettingSection = RetentionSettingSection;
  campaign: Campaign;
  isSaving: boolean = false;
  public availableActionHoldoffs: { id: number; text: string }[] = AvailableActionHoldoffs.slice(0);
  public availableActionHoldOffIntents: { id: number; text: string }[] = AvailableActionHoldOffIntents.slice(0);
  public itemStatusSettings = {
    enableCheckAll: false,
    maxHeight: 140,
  };
  user: User;
  retentionAttemptLimitsEnabled: boolean = false;
  coolDownBetweenAttemptsEnabled: boolean = false;
  productSwapLimitsEnabled: boolean = false;
  cancellationReasonsOptional: boolean = false;
  legalAccepted: boolean = false;

  customerStages: {
    label: string;
    intents: OfferIntents[];
    offerIds: number[];
    enabled: boolean;
  }[] = [
    {
      label: OfferStages[OfferIntents.CancelSubscription],
      intents: [OfferIntents.CancelSubscription],
      offerIds: [],
      enabled: false,
    },
    {
      label: OfferStages[OfferIntents.CancelSubscriptionHold],
      intents: [OfferIntents.CancelSubscriptionHold],
      offerIds: [],
      enabled: false,
    },
    {
      label: OfferStages[OfferIntents.CancelRebillHold],
      intents: [OfferIntents.CancelRebillHold],
      offerIds: [],
      enabled: false,
    },
    {
      label: OfferStages[OfferIntents.CancelOrder],
      intents: [OfferIntents.CancelOrder],
      offerIds: [],
      enabled: false,
    },
    {
      label: 'Trial (if applicable)',
      intents: [OfferIntents.CancelTrial, OfferIntents.CancelTrialHold],
      offerIds: [],
      enabled: false,
    },
    {
      label: OfferStages[OfferIntents.ReturnOrder],
      intents: [OfferIntents.ReturnOrder],
      offerIds: [],
      enabled: false,
    },
    {
      label: OfferStages[OfferIntents.ReturnSubscription],
      intents: [OfferIntents.ReturnSubscription],
      offerIds: [],
      enabled: false,
    },
    {
      label: OfferStages[OfferIntents.ReturnTrial],
      intents: [OfferIntents.ReturnTrial],
      offerIds: [],
      enabled: false,
    },
  ];

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected location: Location,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected campaignService: CampaignService,
    protected userService: UserService,
    protected funnelService: FunnelService,
    protected loader: LoaderService,
    private ngxSmartModalService: NgxSmartModalService
  ) {
    super(alertService, router, location);
  }

  ngOnInit() {
    this.currentSection = this.route.snapshot.data['section'];
    this.filterCustomerStages();
    this.initializeForm();
    this.loadCampaign();
    this.fetchUser();
    this.loadRetentionOffers();
  }

  filterCustomerStages(): void {
    this.customerStages = this.customerStages.filter((stage) => this.stageMatchesSection(stage));
  }

  stageMatchesSection(stage: { intents: OfferIntents[] }): boolean {
    return stage.intents.some((intent) => getOfferBuilderSection(intent) === this.currentSection);
  }

  get visibleCustomerStages() {
    return this.customerStages.filter((stage) => stage.offerIds.length > 0);
  }

  initializeForm() {
    this.form = this.formBuilder.group({
      action_holdoff_delay: [0, [Validators.required, Validators.min(0), Validators.max(10000)]],
      holdoff_actions_config: this.formBuilder.array([]),
    });
  }

  fetchUser() {
    this.userService.getCurrent().subscribe((user: User) => {
      this.user = user;
    });
  }

  loadCampaign() {
    this.campaign = this.campaignService.getCurrentCampaign();
    if (this.campaign) {
      // Load action_holdoff_delay from campaign (convert seconds to hours)
      if (this.campaign.action_holdoff_delay) {
        this.form.patchValue({
          action_holdoff_delay: this.campaign.action_holdoff_delay / 3600,
        });
      }

      // Load holdoff_actions_config from portal_settings
      if (this.campaign.portal_settings) {
        this.loadHoldoffActionsConfig(this.campaign.portal_settings.holdoff_actions_config);
      }

      // Enable Cool Down Between Attempts toggle if there's existing data
      if (
        (this.campaign.action_holdoff_delay && this.campaign.action_holdoff_delay > 0) ||
        (this.campaign.portal_settings &&
          this.campaign.portal_settings.holdoff_actions_config &&
          Object.keys(this.campaign.portal_settings.holdoff_actions_config).length > 0)
      ) {
        this.coolDownBetweenAttemptsEnabled = true;
      }
    }
  }

  loadRetentionOffers() {
    if (this.campaign) {
      this.loader.show();
      this.funnelService
        .listRetentionOffers(this.campaign.id, null, OfferTypes.Downsell)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response: Pager) => {
            const offers = response.results as RetentionOffer[];

            // Map offers to customer stages
            offers.forEach((offer: RetentionOffer) => {
              const stage = this.customerStages.find((s) => s.intents.includes(offer.offer_intent));

              if (stage) {
                stage.offerIds.push(typeof offer.id === 'string' ? parseInt(offer.id, 10) : offer.id);
                stage.enabled = offer.enabled || false;
              }
            });

            if (!this.isSectionExpanded(this.RetentionSettingSection.CustomerStageSetting)) {
              this.toggleSection(RetentionSettingSection.CustomerStageSetting);
            }
            this.loader.hide();
          },
          (error) => {
            this.loader.hide();
            this.handleError(error);
          }
        );
    }
  }

  toggleStageEnabled(stage: any) {
    if (stage && stage.offerIds.length > 0) {
      const isEnabled = !stage.enabled;
      const confirmMessage = isEnabled
        ? 'Are you sure you want to enable this stage?'
        : 'Are you sure you want to disable this stage?';

      if (confirm(confirmMessage)) {
        this.loader.show();

        const handleSuccess = () => {
          stage.enabled = isEnabled;
          this.loader.hide();
          const message = isEnabled ? 'Stage enabled successfully' : 'Stage disabled successfully';
          this.alertService.success(message, true);
        };

        const handleError = (error: any) => {
          this.loader.hide();
          this.handleError(error);
        };

        if (stage.offerIds.length > 1) {
          forkJoin(
            stage.offerIds.map((offerId: number) =>
              this.funnelService.patch(offerId, { enabled: isEnabled, resourcetype: 'ProductFunnel' })
            )
          )
            .pipe(takeUntil(this.destroy$))
            .subscribe(handleSuccess, handleError);
        } else {
          this.funnelService
            .patch(stage.offerIds[0], { enabled: isEnabled, resourcetype: 'ProductFunnel' })
            .pipe(takeUntil(this.destroy$))
            .subscribe(handleSuccess, handleError);
        }
      }
    }
  }

  loadHoldoffActionsConfig(holdoffConfig: any) {
    if (holdoffConfig) {
      const holdoff_actions_config = [];
      Object.entries(holdoffConfig).forEach(([intent, holdoff_actions]) => {
        holdoff_actions_config.push({
          intent: parseInt(intent),
          holdoff_actions: this.availableActionHoldoffs.filter(
            (item) => (holdoff_actions as any[]).indexOf(item['id']) > -1
          ),
        });
      });

      if (holdoff_actions_config.length) {
        const holdoffActionsFormArray = this.formBuilder.array(
          holdoff_actions_config.map((config) =>
            this.formBuilder.group({
              intent: [config.intent || null, Validators.required],
              holdoff_actions: [config.holdoff_actions || [], Validators.required],
            })
          )
        );
        this.form.setControl('holdoff_actions_config', holdoffActionsFormArray);
      }
    }
  }

  isSectionExpanded(section: RetentionSettingSection): boolean {
    return this.expandedSection === section;
  }

  toggleSection(section: RetentionSettingSection): void {
    if (this.expandedSection === section) {
      this.expandedSection = null;
    } else {
      this.expandedSection = section;
    }
  }

  addHoldoffAction() {
    if (this.holdoffActionsArray.length < this.availableActionHoldOffIntents.length) {
      this.holdoffActionsArray.push(
        this.formBuilder.group({
          intent: [null, Validators.required],
          holdoff_actions: [[], Validators.required],
        })
      );
    }
  }

  removeHoldoffAction(index: number) {
    if (confirm('Are you sure you want to remove this rule?')) {
      this.holdoffActionsArray.removeAt(index);
    }
  }

  saveRetentionControls() {
    this.submitted = true;

    if (!this.form.valid) {
      this.form.markAllAsTouched();
      this.alertService.error('Please fill in all required fields', true);
    } else {
      this.isSaving = true;

      // Transform the holdoff_actions_config to the correct format
      const holdoffConfig = {};
      this.holdoffActionsArray.controls.forEach((group: FormGroup) => {
        const intent = group.get('intent').value;
        const actions = group.get('holdoff_actions').value;
        if (intent && actions && actions.length > 0) {
          holdoffConfig[intent] = actions.map((action: any) => action.id);
        }
      });

      const payload = {
        action_holdoff_delay: this.form.get('action_holdoff_delay').value * 3600, // Convert hours to seconds
        portal_settings: {
          holdoff_actions_config: holdoffConfig,
        },
      };

      this.campaignService
        .patch(this.campaign.id, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response: Campaign) => {
            this.campaign = response;
            this.alertService.success('Retention controls saved successfully', true);
            this.isSaving = false;
          },
          (error) => {
            this.handleError(error);
            this.isSaving = false;
          }
        );
    }
  }

  hasHoldoffFieldError(index: number, fieldName: string): boolean {
    const formGroup = this.holdoffActionsArray.at(index) as FormGroup;
    const control = formGroup.get(fieldName);

    // Check if any field in this row is touched
    const anyFieldTouched = Object.keys(formGroup.controls).some((key) => formGroup.get(key).touched);

    return !!(control && control.errors && (control.touched || anyFieldTouched || this.submitted));
  }

  get selectedIntents(): number[] {
    return this.holdoffActionsArray.controls
      .map((group: FormGroup) => +group.get('intent').value)
      .filter((intent) => intent !== null);
  }

  get holdoffActionsArray(): FormArray {
    return this.form.get('holdoff_actions_config') as FormArray;
  }

  get isAdmin(): boolean {
    return this.user && this.user.is_staff;
  }

  onCancellationReasonsToggle(event: Event): void {
    event.preventDefault();
    this.legalAccepted = false;
    this.ngxSmartModalService.getModal('legalResponsibilityPopup').open();
  }

  proceedSurveySetting(): void {
    this.cancellationReasonsOptional = !this.cancellationReasonsOptional;
    this.legalAccepted = false;
    this.ngxSmartModalService.getModal('legalResponsibilityPopup').close();
  }

  cancelSurveySetting(): void {
    this.legalAccepted = false;
    this.ngxSmartModalService.getModal('legalResponsibilityPopup').close();
  }

  get sectionName(): string {
    return OfferBuilderSectionNames[this.currentSection];
  }

  get bannerTitle(): string {
    return `${this.sectionName} Settings`;
  }

  get bannerSubtitle(): string {
    return `${this.sectionName} Settings define how ${this.sectionName.toLowerCase()} flows operate across your account, including which customer stages are managed, global limits for downsell attempts, and rules that determine when customers exit automated ${this.sectionName.toLowerCase()} flows.`;
  }
}
